import { onError } from '@apollo/client/link/error';
import { captureException } from '@sentry/react';
import { errorReferenceCodes, getErrorDiagnostics, rememberErrorOperation } from 'helpers/errorDiagnostics';

/** Report actionable API failures once per operation/category per five minutes.
 * Never pass Apollo errors, query documents, variables or server messages to Sentry.
 */
export function createGraphqlErrorLink(report = captureException) {
  const reported = new Map<string, number>();
  return onError(({ graphQLErrors, networkError, operation }) => {
    const operationName = /^[A-Za-z_][A-Za-z0-9_]{0,99}$/.test(operation.operationName) ? operation.operationName : 'anonymous';
    const errors = [...(graphQLErrors || []), networkError];
    rememberErrorOperation(errors, operationName);
    const diagnostics = getErrorDiagnostics({ graphQLErrors, networkError });
    const internalError = graphQLErrors?.some(error => error.extensions?.code === 'INTERNAL_SERVER_ERROR');
    if (diagnostics.kind !== 'schema' && !(diagnostics.httpStatus >= 500) && !internalError) return;

    const key = `${operationName}:${diagnostics.kind}:${diagnostics.httpStatus || 'none'}`;
    const now = Date.now();
    for (const [entry, timestamp] of reported) {
      if (now - timestamp >= 300000) reported.delete(entry);
    }
    if (reported.has(key)) return;
    if (reported.size >= 100) reported.delete(reported.keys().next().value);
    reported.set(key, now);
    const eventId = report(new Error(`ARK API ${diagnostics.kind} failure`), {
      tags: { error_reference: errorReferenceCodes[diagnostics.kind], api_failure_kind: diagnostics.kind, graphql_operation: operationName, http_status: String(diagnostics.httpStatus || 'unknown') },
      fingerprint: ['ark-api', operationName, diagnostics.kind, String(diagnostics.httpStatus || 'unknown')],
    });
    if (typeof eventId === 'string' && /^[a-f0-9]{32}$/i.test(eventId)) rememberErrorOperation(errors, operationName, eventId);
    // No forward(operation): replaying a failed mutation may duplicate writes.
  });
}
